import { nextTick } from 'vue';
import type { Router, RouterScrollBehavior } from 'vue-router';

// The app shell does not scroll the document — a container inside it does. vue-router's
// `savedPosition` and a returned `{ top }` both address the window, which therefore never moves:
// switching from a long list to a short view left the reader somewhere below the fold. Angular
// solves the identical problem by replacing ViewportScroller with
// AppViewportScroller.setScrollableElement(el); this is the Vue equivalent and keeps Angular's
// `scrollPositionRestoration: 'enabled'` semantics — restore on history navigation, top on every
// other.
//
// Which element scrolls differs per app, mirroring what each Angular app passes to
// setScrollableElement(): the four page-layout apps hand it `mainLayout` in their app.component,
// club-member-registration hands it the wizard shell's `#content`.
const PAGE_LAYOUT_CONTAINER = '.kgr-pageLayout__main';

export interface ScrollRestoration {
    trackScrollPositions: (router: Router) => void
    scrollBehavior: RouterScrollBehavior
}

/**
 * Builds the scroll-restoration pair for one scrollable container.
 *
 * @param selector Element that actually scrolls; falls back to the window when it is not in the DOM.
 */
export function createScrollRestoration(selector: string): ScrollRestoration {
    // Keyed by fullPath, mirroring what vue-router does for the window. Per instance, so two
    // containers never overwrite each other's offsets.
    const positions = new Map<string, number>();

    const container = (): HTMLElement | null => document.querySelector<HTMLElement>(selector);

    return {
        // Records the scroll offset of the view being left. Must run in a `beforeEach` — at that
        // point the outgoing view is still rendered, so the container still holds its offset.
        trackScrollPositions(router: Router): void {
            router.beforeEach((_to, from) => {
                const el = container();
                if (el) {
                    positions.set(from.fullPath, el.scrollTop);
                }
            });
        },

        scrollBehavior: async (to, _from, savedPosition): Promise<false> => {
            // savedPosition is non-null exactly for history navigation (back/forward) — the case
            // Angular restores. Its value is unusable here (window), the fullPath is what we key on.
            const top = savedPosition ? (positions.get(to.fullPath) ?? 0) : 0;

            // Let the incoming view render first, otherwise the container is still the outgoing
            // view's height and a restore would be clamped to it.
            await nextTick();

            const el = container();
            if (el) {
                el.scrollTop = top;
            } else {
                window.scrollTo(0, top);
            }

            // Falsy return: we scrolled ourselves, vue-router must not touch the window on top of it.
            return false;
        },
    };
}

// Pre-bound to the page-layout shell: the existing call sites keep importing these two directly.
export const { trackScrollPositions, scrollBehavior } = createScrollRestoration(PAGE_LAYOUT_CONTAINER);
